/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { Global, Theme } from "@emotion/react";
import joinSVG from "assets/images/join-in-on-the-hug.svg";
import inclusiverseSVG from "assets/images/step-into-the-inclusiverse.svg";
import gemSVG from "assets/images/gem.svg";
import globeSVG from "assets/images/globe.svg";
import hugTokenSVG from "assets/images/hug-token.svg";
import hughubSVG from "assets/images/hughub.svg";
import logoSVG from "assets/images/logo.svg";
import membershipPassSVG from "assets/images/membership-pass.svg";
import paletteSVG from "assets/images/palette.svg";
import welcomeSVG from "assets/images/welcome-to-the-inclusiverse.svg";
import alphaSVG from "assets/images/alpha-only.svg";
import huggedSVG from "assets/images/hugged-not-rugged.svg";
import sparkleSVG from "assets/images/sparkles.svg";
import groupHug0001PNG from "assets/images/groupHUG0001.png";
import groupHug0002PNG from "assets/images/groupHUG0002.png";
import groupHugResidencePNG from "assets/images/artists-in-residence.png";
import { Callout, Container, H1, H2, Link, Text } from "atoms";
import { constants } from "config";
import { ArtistCard, ArtistCardProps } from "./ArtistCard";
import { HomeAccordion } from "./HomeAccordion";
import featured from "./featured.json";

const css = {
  header: (theme: Theme) => ({
    background: [theme.colors.pink, theme.gradients.radial1],
    "& > div": {},
  }),
  heroImg: (theme: Theme) => ({
    marginBottom: "2rem",
    maxWidth: 250,
    width: "50vw",
    [theme.breakpoints.desktop]: {
      margin: 0,
      maxWidth: 550,
      width: "30vw",
    },
  }),
  sticker: {
    position: "absolute",
    zIndex: 0,
  } as const,
  welcomeSticker: {
    left: "5vmin",
    transform: "rotate(-17deg)",
    top: "5vmin",
    width: "18vmin",
  },
  huggedSticker: {
    bottom: "5vmin",
    right: "21.5vmin",
    transform: "rotate(-9deg)",
    width: "22vmin",
  },
  alphaSticker: {
    bottom: "11.5vmin",
    right: "5vmin",
    transform: "rotate(15deg)",
    width: "18vmin",
  },
  sparkleSticker1: {
    top: 0,
    left: "-4rem",
    maxWidth: 95,
    width: "16%",
    "@media (max-width: 750px)": {
      display: "none",
    },
  },
  sparkleSticker2: {
    top: "min(11rem, 10vw)",
    right: "-7rem",
    maxWidth: 95,
    width: "16%",
    "@media (max-width: 750px)": {
      display: "none",
    },
  },
  inclusiverse: (theme: Theme) => ({
    background: [theme.colors.bg, theme.gradients.radial2],
    "& > div": {},
  }),
  hughubContent: {
    margin: "3rem 0",
    img: {
      height: "4rem",
    },
    "@media (min-width: 900px)": {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      div: {
        width: "30%",
      },
    },
  } as const,
  grouphugContent: (theme: Theme) =>
    ({
      margin: "3rem 0",
      "& > div": {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        margin: "2rem 0",
        img: {
          borderRadius: theme.borderRadius.md,
          width: "32%",
        },
      },
    } as const),
  accordionColumns: (theme: Theme) => ({
    margin: "3rem 0",
    "@media (min-width: 800px)": {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gridGap: theme.spacing.md,
    },
    "@media (min-width: 1000px)": {
      gridTemplateColumns: "repeat(3, 1fr)",
      gridGap: theme.spacing.lg,
    },
  }),
  featured: (theme: Theme) =>
    ({
      background: theme.colors.bg,
      "& > header": {
        padding: "2rem",
        textAlign: "center",
        maxWidth: 1440,
        margin: "0 auto",
        h2: { margin: 0 },
        "@media (min-width: 1000px)": {
          alignItems: "baseline",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          textAlign: "left",
          p: { margin: 0 },
        },
      },
      "& > div": {
        "@media (min-width: 680px)": {
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          margin: "0 auto",
          maxWidth: 1440,
        },
      },
    } as const),
  featuredArtist: {
    "@media (min-width: 680px)": {
      flex: 1,
      width: "32%",
    },
  },
  roadmap: (theme: Theme) => ({
    background: [theme.colors.pink, theme.gradients.radial3],
  }),
  roadmapCallout: {
    padding: "3rem 2rem",
    "section + section": { marginTop: "3rem" },
    img: {
      marginBottom: "1rem",
      height: "5rem",
      width: "5rem",
    },
    "@media (min-width: 1000px)": {
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      section: {
        width: "50%",
        padding: "0 2rem",
      },
      "section + section": { marginTop: 0 },
    },
  } as const,
  community: {
    textAlign: "center" as const,
    padding: "6rem 2rem",
  },
  global: (theme: Theme) => ({
    "html,body": {
      background: theme.colors.pink,
    },
  }),
};

function CorporateHome() {
  return (
    <main>
      <Global styles={css.global} />
      <header css={css.header}>
        <HeaderContainer>
          <img
            src={welcomeSVG}
            alt=""
            aria-hidden="true"
            role="presentation"
            css={[css.sticker, css.welcomeSticker]}
          />
          <img
            src={huggedSVG}
            alt=""
            aria-hidden="true"
            role="presentation"
            css={[css.sticker, css.huggedSticker]}
          />
          <img
            src={alphaSVG}
            alt=""
            aria-hidden="true"
            role="presentation"
            css={[css.sticker, css.alphaSticker]}
          />
          <img src={joinSVG} alt="Join in on the Hug!" css={css.heroImg} />

          <div>
            <H1>Welcome to HUG</H1>
            <Text size="lg">
              a new inclusiverse for creators and collectors who are building,
              collaborating, discovering, and embracing Web3.
            </Text>
          </div>
        </HeaderContainer>
      </header>

      <article css={css.inclusiverse}>
        <InclusiverseContainer>
          <header>
            <img
              src={sparkleSVG}
              alt=""
              role="presentation"
              css={[css.sticker, css.sparkleSticker1]}
            />
            <img
              src={sparkleSVG}
              alt=""
              role="presentation"
              css={[css.sticker, css.sparkleSticker2]}
            />
            <img src={inclusiverseSVG} alt="Step into the Inclusiverse" />

            <Text size="lg">
              We know Web3 can be overwhelming for creators and collectors, so
              we’re changing that in three ways.
            </Text>
          </header>

          <HomeAccordion
            title="HUGhub"
            description="The HUG is the world's friendliest community for discovering hug-worthy NFTs through community-sourced reviews. Why DYOR alone when you can do it with the rest of your HUGfam?"
          >
            <div css={css.hughubContent}>
              <div>
                <img src={gemSVG} alt="" />
                <H2>For Collectors</H2>
                <Text size="lg">
                  Discover new projects and artists to love while writing and
                  reading community-sourced reviews, all in one place.
                </Text>
              </div>
              <div>
                <img src={paletteSVG} alt="" />
                <H2>For Creators</H2>
                <Text size="lg">
                  Find other creators to collaborate with and get discovered by
                  collectors looking for projects or art like yours.
                </Text>
              </div>
              <div>
                <img src={globeSVG} alt="" />
                <H2>For Everyone</H2>
                <Text size="lg">
                  With our HUG Membership Pass, access huggable, collectible
                  artwork and entry into the HUG Discord, including special
                  access to a growing community research platform.
                </Text>
              </div>
            </div>
          </HomeAccordion>

          <HomeAccordion
            title="groupHUG"
            description="groupHUG is a Web3 accelerator for post-mint NFT projects and up-and-coming 1/1 artists who have established a product-community fit and are ready for long-term growth and financial success."
          >
            <div css={css.accordionColumns}>
              <div>
                <H2>A full year of support</H2>
                <Text size="lg">
                  Twelve months of mentorship and advice from the HUG team
                </Text>
              </div>
              <div>
                <H2>6 week level up</H2>
                <Text size="lg">
                  An intensive curriculum focusing on positioning for long-term
                  success
                </Text>
              </div>
              <div>
                <H2>1:1 advising</H2>
                <Text size="lg">
                  Office hours with HUG’s senior advisors and founding team
                </Text>
              </div>
              <div>
                <H2>Access to experts</H2>
                <Text size="lg">
                  Sessions with industry experts and advisors
                </Text>
              </div>
              <div>
                <H2>Relationships</H2>
                <Text size="lg">
                  Partnership opportunities across the HUG ecosystem and
                  friendships for life
                </Text>
              </div>
              <div>
                <H2>PR and marketing exposure</H2>
                <Text size="lg">
                  Amplification across HUG’s social channels with an audience of
                  over 300k
                </Text>
              </div>
            </div>

            <div css={css.grouphugContent}>
              <H2>Meet the Cohorts</H2>
              <Text size="lg">
                We’re proud to work with best-in-class NFT creators and artists
                to grow their businesses on the cutting edge of Web3. Creators
                move in cohorts, as we believe that peer mentorship is crucial
                to success. Check them out!
              </Text>

              <div>
                <img
                  src={groupHug0001PNG}
                  alt="Example art of groupHUG 0001 cohort"
                />
                <img
                  src={groupHug0002PNG}
                  alt="Example art of groupHUG 0002 cohort"
                />
                <img
                  src={groupHugResidencePNG}
                  alt="Example art of Artists in Residence"
                />
              </div>
            </div>
          </HomeAccordion>

          <HomeAccordion
            title="hedgeHUG"
            description="hedgeHUG is a hands-on advisory program for a limited number of early stage pre-mint projects where we leverage our creativity and the love across our community and social channels to lay the groundwork for long-term success, from mint and beyond."
          >
            <div css={css.accordionColumns}>
              <div>
                <H2>A team that has your back</H2>
                <Text size="lg">
                  3 months of advice and mentorship from the HUG team
                </Text>
              </div>
              <div>
                <H2>2 week level up</H2>
                <Text size="lg">
                  An intensive curriculum to shape your strategy from mint to
                  beyond
                </Text>
              </div>
              <div>
                <H2>1:1 advising</H2>
                <Text size="lg">
                  Office hours with HUG’s senior advisors and founding team
                </Text>
              </div>
              <div>
                <H2>Personalized support</H2>
                <Text size="lg">
                  Real-time strategic guidance and community support in the
                  leadup to your mint
                </Text>
              </div>
              <div>
                <H2>Relationships</H2>
                <Text size="lg">
                  Partnership opportunities across the HUG ecosystem and
                  friendships for life
                </Text>
              </div>
              <div>
                <H2>PR and marketing exposure</H2>
                <Text size="lg">
                  Amplification across HUG’s social channels with an audience of
                  over 300k
                </Text>
              </div>
            </div>
          </HomeAccordion>
        </InclusiverseContainer>
      </article>

      <article css={css.featured}>
        <header>
          <H2>Featured Creators</H2>
          <Text size="lg">groupHUG artists-in-residence</Text>
          {/* <Button variant="gradient">See all creators</Button> */}
        </header>

        <div>
          {(featured.artists as ArtistCardProps[]).map((artist) => (
            <ArtistCard
              key={artist.handle}
              {...artist}
              css={css.featuredArtist}
            />
          ))}
        </div>
      </article>

      <article css={css.roadmap}>
        <RoadmapContainer>
          <header>
            <H1>HUG Roadmap</H1>
            <Text size="lg">
              Here is what you can expect from HUG in the coming months.
            </Text>
          </header>

          <Callout css={css.roadmapCallout}>
            <section>
              <img src={hughubSVG} alt="" />
              <H2>The HUG</H2>
              <Text size="lg">
                A community platform (initially available on the HUG Discord)
                for discovering, researching, and curating NFTs.
              </Text>
            </section>
            <section>
              <img src={membershipPassSVG} alt="" />
              <H2>HUG Membership Pass</H2>
              <Text size="lg">
                Our HUG Membership Pass NFT that features huggable artwork by{" "}
                <Link href="https://twitter.com/lisachownft">Lisa Chow</Link>{" "}
                and grants you entry to the HUG (initially available on
                Discord), including special access to our growing community
                research platform.
              </Text>
            </section>
            <section>
              <img src={hugTokenSVG} alt="" />
              <H2>Community Engagement Benefits</H2>
              <Text size="lg">
                Anticipated special perks and innovative rewards intended to
                recognize high-quality DYOR in community with others, including
                opportunities for forming bonds with the HUGfam online and IRL.
              </Text>
            </section>
            <section>
              <img src={logoSVG} alt="" />
              <H2>Premium HUG Membership Tiers</H2>
              <Text size="lg">
                Expected premium membership tiers that we plan to couple with
                exciting benefits.
              </Text>
            </section>
          </Callout>
        </RoadmapContainer>
      </article>

      <article css={css.community}>
        <H1>Join the HUG Community</H1>
        <Text size="lg">
          It’s always a good day to enter the inclusiverse. 🌎✨
        </Text>
        <Link href={constants.discordURL} variant="gradient" arrow="dr">
          Join The Discord
        </Link>
      </article>

      <article css={css.featured}>
        <div>
          {(featured.projects as ArtistCardProps[]).map((project) => (
            <ArtistCard
              key={project.handle}
              {...project}
              css={css.featuredArtist}
            />
          ))}
        </div>
      </article>
    </main>
  );
}

const HeaderContainer = styled(Container)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: "18vmin 2rem 25vmin",
  position: "relative",
  maxWidth: 1600,
  minHeight: "calc(100vh - 60px)",
  textAlign: "center",
  [theme.breakpoints.desktop]: {
    minHeight: "calc(100vh - 75px)",
    flexDirection: "row",
    padding: "18vmin 3vw 25vmin",
    textAlign: "left",
  },
  "& *": {
    zIndex: 1,
  },
  "& div": {
    zIndex: 1,
    [theme.breakpoints.desktop]: {
      width: "50vw",
      maxWidth: 600,
      marginLeft: "5vw",
    },
  },
}));

const InclusiverseContainer = styled(Container)({
  padding: "5rem 2rem",
  "@media (min-width: 750px)": {
    padding: "5rem",
  },
  "& > header": {
    textAlign: "center",
    position: "relative",
    maxWidth: 800,
    margin: "0 auto",
    "img:not([role=presentation])": {
      margin: "0 auto 2rem",
      width: "100%",
    },
    p: {
      margin: "0 auto 3rem",
      maxWidth: 775,
    },
  },
});

const RoadmapContainer = styled(Container)({
  padding: "5rem 2rem",
  "@media (min-width: 1000px)": {
    padding: "5rem",
  },
  header: {
    textAlign: "center",
    marginBottom: "4rem",
  },
});

export { CorporateHome };
